import type { UseMutationOptions } from '@tanstack/react-query';
import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  getPlusApplicant,
  patchPartialPlusApplicant,
  patchPlusApplicantRecommender,
  patchPlusFinalApplicant,
  postPlusApplicant,
} from '@/api/plus/applicant';
import type {
  GetPlusApplicantResponseDto,
  PatchPlusApplicantRecommenderRequestDto,
  PatchPlusApplicantRequestDto,
  PostPlusApplicantRequestDto,
} from '@/api/plus/applicant/applicant.dto';
import { PLUS_COURSE_DETAIL } from '@/constants';
import { sendGTM } from '@/lib/sdk/google-tag-manager';
import { removeLocalStorageItem } from '@/lib/utils/local-storage';
import { sendLog } from '@/lib/utils/send-log';
import type { Course, PlusCourse, PlusCourseRoute } from '@/types';
import { sendApplyFormSubmitCompletedLogToBraze } from '@/lib/utils/businessLogics/logs/braze';

export const plusApplicantQueryKeys = {
  all: ['plus-applicant'],
  applicantByOnlineUserIdAndRoundId: (
    onlineUserId: string,
    course: Omit<Course, '취업 리부트 코스'>,
    roundId?: string,
  ) => [
    ...plusApplicantQueryKeys.all,
    onlineUserId,
    course,
    roundId ?? 'LATEST',
  ],
};

export function useGetPlusApplicant({
  onlineUserId,
  course,
  roundId,
}: {
  onlineUserId: string;
  course: PlusCourseRoute;
  roundId?: string;
}) {
  const courseKoreanName: PlusCourse = PLUS_COURSE_DETAIL[course].koreanName;

  return useQuery({
    queryKey: plusApplicantQueryKeys.applicantByOnlineUserIdAndRoundId(
      onlineUserId,
      courseKoreanName,
      roundId,
    ),
    queryFn: () => getPlusApplicant(onlineUserId, courseKoreanName, roundId),
  });
}

export function useCreatePlusApplicant(courseName: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: PostPlusApplicantRequestDto) =>
      postPlusApplicant({ body, courseName }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: plusApplicantQueryKeys.applicantByOnlineUserIdAndRoundId(
          response.user.onlineUserId,
          response.course.name,
          response.round._id,
        ),
      });
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: plusApplicantQueryKeys.all,
      });
      alert(
        '지원서 제출에 실패했습니다. 다시 시도해주세요. 문제가 지속될 경우 고객센터로 문의해주세요.',
      );
    },
  });
}

export function usePatchPlusFinalApplicant({
  onlineUserId,
  course,
  localStorageKey,
}: {
  onlineUserId: string;
  course: PlusCourseRoute;
  localStorageKey?: string;
}) {
  const queryClient = useQueryClient();
  const { koreanName, type, koreanPosition } = PLUS_COURSE_DETAIL[course] || {};
  return useMutation({
    mutationKey: plusApplicantQueryKeys.all,
    mutationFn: (body: PatchPlusApplicantRequestDto) =>
      patchPlusFinalApplicant({ body }),
    onSuccess: async (updatedApplicant) => {
      await queryClient.invalidateQueries({
        queryKey: plusApplicantQueryKeys.applicantByOnlineUserIdAndRoundId(
          onlineUserId,
          koreanName,
          updatedApplicant.round._id,
        ),
      });
      const applicant = queryClient.getQueryData<GetPlusApplicantResponseDto>(
        plusApplicantQueryKeys.applicantByOnlineUserIdAndRoundId(
          onlineUserId,
          koreanName,
          updatedApplicant.round._id,
        ),
      );
      sendGTM({
        event: 'hh_application1_completed',
        online_user_id: onlineUserId,
        email: applicant?.user?.email,
        phone: `+82${applicant?.user?.phone?.substring(1)}`,
        content_category: 'hanghae',
        content_type: type,
        content_name: '',
      });
      sendLog('hh_apply_formSubmit_completed', {
        ...applicant,
        course_type: koreanPosition,
      });
      sendApplyFormSubmitCompletedLogToBraze(
        course,
        updatedApplicant.round.name,
      );
      removeLocalStorageItem(
        localStorageKey ?? `hanghae-plus-${course}-applicant-${onlineUserId}`,
      );
    },
  });
}

export function usePatchPartialPlusApplicant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: plusApplicantQueryKeys.all,
    mutationFn: patchPartialPlusApplicant,
    onSuccess: async (applicant) => {
      await queryClient.invalidateQueries({
        queryKey: plusApplicantQueryKeys.applicantByOnlineUserIdAndRoundId(
          applicant.user.onlineUserId,
          applicant.course.name,
          applicant.round._id,
        ),
      });
    },
  });
}

export function usePatchPlusApplicantRecommender(
  mutateOptions: UseMutationOptions<
    void,
    unknown,
    PatchPlusApplicantRecommenderRequestDto
  >,
) {
  return useMutation({
    mutationFn: patchPlusApplicantRecommender,
    ...mutateOptions,
  });
}

export function useIsMutatingPlusApplicant() {
  const mutationCount = useIsMutating({
    mutationKey: plusApplicantQueryKeys.all,
  });

  return mutationCount > 0;
}
